/**
 * Zustand store for Tasks (Kanban board)
 */
import { create } from 'zustand';
import { Task, TaskStatus, TaskStats } from '../types/task';
import { tasksAPI } from '../services/api';
import { getErrorMessage } from '../utils/errorMessages';

interface TaskStore {
  tasks: Task[];
  stats: TaskStats | null;
  loading: boolean;
  error: string | null;

  // Actions
  fetchTasks: (projectId: number) => Promise<void>;
  fetchStats: (projectId: number) => Promise<void>;
  createTask: (task: Partial<Task>) => Promise<Task | null>;
  updateTask: (taskId: number, updates: Partial<Task>) => Promise<Task | null>;
  updateTaskStatus: (taskId: number, status: TaskStatus, notes?: string) => Promise<Task | null>;
  deleteTask: (taskId: number) => Promise<boolean>;
  autoGenerateTasks: (projectId: number) => Promise<void>;
}

export const useTaskStore = create<TaskStore>((set, get) => ({
  tasks: [],
  stats: null,
  loading: false,
  error: null,

  fetchTasks: async (projectId: number) => {
    set({ loading: true, error: null });
    try {
      const tasks = await tasksAPI.getProjectTasks(projectId);
      set({ tasks, loading: false });
    } catch (error: any) {
      console.error('[TaskStore] Error fetching tasks:', error);
      set({ error: getErrorMessage(error, 'Nepodarilo sa načítať úlohy.'), loading: false });
    }
  },

  fetchStats: async (projectId: number) => {
    try {
      const stats = await tasksAPI.getProjectStats(projectId);
      set({ stats });
    } catch (error: any) {
      console.error('[TaskStore] Error fetching stats:', error);
    }
  },

  createTask: async (task: Partial<Task>) => {
    set({ loading: true, error: null });
    try {
      const newTask = await tasksAPI.createTask(task);
      set((state) => ({
        tasks: [...state.tasks, newTask],
        loading: false,
      }));
      return newTask;
    } catch (error: any) {
      console.error('[TaskStore] Error creating task:', error);
      set({ error: getErrorMessage(error, 'Nepodarilo sa vytvoriť úlohu.'), loading: false });
      return null;
    }
  },

  updateTask: async (taskId: number, updates: Partial<Task>) => {
    set({ loading: true, error: null });
    try {
      const updatedTask = await tasksAPI.updateTask(taskId, updates);
      set((state) => ({
        tasks: state.tasks.map((t) => (t.id === taskId ? updatedTask : t)),
        loading: false,
      }));
      return updatedTask;
    } catch (error: any) {
      console.error('[TaskStore] Error updating task:', error);
      set({ error: getErrorMessage(error, 'Nepodarilo sa uložiť zmeny úlohy.'), loading: false });
      return null;
    }
  },

  updateTaskStatus: async (taskId: number, status: TaskStatus, notes?: string) => {
    try {
      const updatedTask = await tasksAPI.updateTaskStatus(taskId, status, notes);
      set((state) => ({
        tasks: state.tasks.map((t) => (t.id === taskId ? updatedTask : t)),
      }));
      return updatedTask;
    } catch (error: any) {
      console.error('[TaskStore] Error updating task status:', error);
      set({ error: getErrorMessage(error, 'Nepodarilo sa zmeniť stav úlohy.') });
      return null;
    }
  },

  deleteTask: async (taskId: number) => {
    set({ loading: true, error: null });
    try {
      await tasksAPI.deleteTask(taskId);
      set((state) => ({
        tasks: state.tasks.filter((t) => t.id !== taskId),
        loading: false,
      }));
      return true;
    } catch (error: any) {
      console.error('[TaskStore] Error deleting task:', error);
      set({ error: getErrorMessage(error, 'Nepodarilo sa zmazať úlohu.'), loading: false });
      return false;
    }
  },

  autoGenerateTasks: async (projectId: number) => {
    set({ loading: true, error: null });
    try {
      await tasksAPI.autoGenerateTasks(projectId);
      // Refresh tasks after generation
      await get().fetchTasks(projectId);
    } catch (error: any) {
      console.error('[TaskStore] Error auto-generating tasks:', error);
      set({ error: getErrorMessage(error, 'Nepodarilo sa automaticky vygenerovať úlohy.'), loading: false });
    }
  },
}));

